import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save, Settings } from '@mui/icons-material';
import { alertsApi } from '../api';
import { useI18n } from '../i18n';
import {
  AlertChannelsDraft,
  AlertChannelsPublic,
  draftFromPublic,
  emptyChannelsDraft,
} from '../alerting/types';

const SettingsPage: React.FC = () => {
  const { t } = useI18n();
  const [channels, setChannels] = useState<AlertChannelsDraft>(emptyChannelsDraft());
  const [savedChannels, setSavedChannels] = useState<AlertChannelsDraft>(emptyChannelsDraft());
  const [tokenSet, setTokenSet] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);
  const [pushoverConfigured, setPushoverConfigured] = useState(false);
  const [mailConfigured, setMailConfigured] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(channels) !== JSON.stringify(savedChannels);

  const applyChannels = (doc: {
    channels?: AlertChannelsPublic;
    pushoverConfigured?: boolean;
    mailConfigured?: boolean;
  }) => {
    const draft = draftFromPublic(doc.channels);
    setChannels(draft);
    setSavedChannels(draft);
    setTokenSet(Boolean(doc.channels?.pushover?.tokenSet));
    setPasswordSet(Boolean(doc.channels?.mail?.passwordSet));
    setPushoverConfigured(doc.pushoverConfigured === true);
    setMailConfigured(doc.mailConfigured === true);
  };

  const load = useCallback(async () => {
    try {
      setError(null);
      applyChannels(await alertsApi.get());
    } catch (err) {
      console.error(err);
      setError(t('settings.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const channelPayload = () => ({
    pushover: {
      enable: channels.pushover.enable,
      user: channels.pushover.user,
      token: channels.pushover.token,
    },
    mail: {
      enable: channels.mail.enable,
      host: channels.mail.host,
      port: channels.mail.port,
      secure: channels.mail.secure,
      user: channels.mail.user,
      password: channels.mail.password,
      from: channels.mail.from,
      to: channels.mail.to,
    },
  });

  const handleSave = async () => {
    try {
      setBusy(true);
      setError(null);
      applyChannels(await alertsApi.save({ channels: channelPayload() }));
      setStatus(t('settings.saved'));
    } catch (err) {
      console.error(err);
      setError(t('settings.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async (channel: 'pushover' | 'mail') => {
    try {
      setBusy(true);
      setError(null);
      await alertsApi.test(channel, channelPayload());
      setStatus(channel === 'mail' ? t('alerting.testMailSent') : t('alerting.testPushoverSent'));
    } catch (err: unknown) {
      console.error(err);
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (message === 'not_configured' || message === 'pushover_not_configured') {
        setError(t('alerting.pushoverNotConfigured'));
      } else if (message === 'mail_not_configured') {
        setError(t('alerting.mailNotConfigured'));
      } else {
        setError(channel === 'mail' ? t('alerting.testMailFailed') : t('alerting.testPushoverFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={1}>
          <Settings color="primary" />
          <Typography variant="h5">{t('settings.title')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={busy || !dirty}>
          {t('common.save')}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('settings.subtitle')}
      </Typography>

      {!pushoverConfigured && !mailConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }}>{t('alerting.notConfigured')}</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {status && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStatus(null)}>{status}</Alert>}
      {dirty && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
          {t('common.unsaved')}
        </Typography>
      )}

      <Typography variant="subtitle1">{t('alerting.channels')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('alerting.channelsHint')}
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
        <FormControlLabel
          control={(
            <Switch
              checked={channels.pushover.enable}
              onChange={(e) => setChannels((prev) => ({
                ...prev,
                pushover: { ...prev.pushover, enable: e.target.checked },
              }))}
            />
          )}
          label={t('alerting.pushoverEnable')}
        />
        <TextField
          size="small"
          label={t('alerting.pushoverUser')}
          value={channels.pushover.user}
          onChange={(e) => setChannels((prev) => ({
            ...prev,
            pushover: { ...prev.pushover, user: e.target.value },
          }))}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <TextField
          size="small"
          type="password"
          autoComplete="new-password"
          label={t('alerting.pushoverToken')}
          placeholder={tokenSet ? t('alerting.secretKept') : undefined}
          value={channels.pushover.token}
          onChange={(e) => setChannels((prev) => ({
            ...prev,
            pushover: { ...prev.pushover, token: e.target.value },
          }))}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <Button variant="outlined" onClick={() => handleTest('pushover')} disabled={busy}>
          {t('alerting.testPushover')}
        </Button>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <FormControlLabel
          control={(
            <Switch
              checked={channels.mail.enable}
              onChange={(e) => setChannels((prev) => ({
                ...prev,
                mail: { ...prev.mail, enable: e.target.checked },
              }))}
            />
          )}
          label={t('alerting.mailEnable')}
        />
        <TextField
          size="small"
          label={t('alerting.mailHost')}
          value={channels.mail.host}
          onChange={(e) => setChannels((prev) => ({
            ...prev,
            mail: { ...prev.mail, host: e.target.value },
          }))}
          sx={{ minWidth: 180, flex: 1 }}
        />
        <TextField
          size="small"
          type="number"
          label={t('alerting.mailPort')}
          value={channels.mail.port}
          onChange={(e) => setChannels((prev) => ({
            ...prev,
            mail: { ...prev.mail, port: Number(e.target.value) || 0 },
          }))}
          sx={{ width: 110 }}
        />
        <FormControlLabel
          control={(
            <Switch
              checked={channels.mail.secure}
              onChange={(e) => setChannels((prev) => ({
                ...prev,
                mail: { ...prev.mail, secure: e.target.checked },
              }))}
            />
          )}
          label={t('alerting.mailSecure')}
        />
        <TextField
          size="small"
          label={t('alerting.mailUser')}
          value={channels.mail.user}
          onChange={(e) => setChannels((prev) => ({
            ...prev,
            mail: { ...prev.mail, user: e.target.value },
          }))}
          sx={{ minWidth: 160, flex: 1 }}
        />
        <TextField
          size="small"
          type="password"
          autoComplete="new-password"
          label={t('alerting.mailPassword')}
          placeholder={passwordSet ? t('alerting.secretKept') : undefined}
          value={channels.mail.password}
          onChange={(e) => setChannels((prev) => ({
            ...prev,
            mail: { ...prev.mail, password: e.target.value },
          }))}
          sx={{ minWidth: 160, flex: 1 }}
        />
        <TextField
          size="small"
          label={t('alerting.mailFrom')}
          value={channels.mail.from}
          onChange={(e) => setChannels((prev) => ({
            ...prev,
            mail: { ...prev.mail, from: e.target.value },
          }))}
          sx={{ minWidth: 180, flex: 1 }}
        />
        <TextField
          size="small"
          label={t('alerting.mailTo')}
          value={channels.mail.to}
          onChange={(e) => setChannels((prev) => ({
            ...prev,
            mail: { ...prev.mail, to: e.target.value },
          }))}
          sx={{ minWidth: 180, flex: 1 }}
        />
        <Button variant="outlined" onClick={() => handleTest('mail')} disabled={busy}>
          {t('alerting.testMail')}
        </Button>
      </Box>
    </Container>
  );
};

export default SettingsPage;
